import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { maxBy } from "@common/helpers/collections";
import { Tag } from "@design-system/Tag/Tag";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { WorkspaceMembersRow } from "../workspaceMembersRow";
import { lastActivityLabel } from "../useMemberLastActivity";

export const LastActiveCell = memo(function LastActiveCell({
    row,
}: CellContext<ViewTableFeatures, WorkspaceMembersRow, unknown>) {
    const data = row.original;

    const memberLastActivity = useMemo(() => {
        if (data.kind !== "member") {
            return null;
        }
        return lastActivityLabel(new Date(data.member.user.lastActivity));
    }, [data]);

    const orgLastActivity = useMemo(() => {
        if (data.kind !== "organization") {
            return null;
        }
        const mostRecent = maxBy(
            data.organizationMembers,
            (m) => m.user.lastActivity,
        );
        return mostRecent
            ? lastActivityLabel(new Date(mostRecent.user.lastActivity))
            : null;
    }, [data]);

    if (data.kind === "invite") {
        return (
            <ViewTableCell>
                <Tag size="sm" color="neutral" outline>
                    Invite pending
                </Tag>
            </ViewTableCell>
        );
    }

    if (data.kind === "organization") {
        return <ViewTableCell>{orgLastActivity}</ViewTableCell>;
    }

    return <ViewTableCell>{memberLastActivity}</ViewTableCell>;
});
